import React, { useCallback, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, TextField, MenuItem, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { SURFACE_TYPE_OPTIONS, COATING_OPTIONS, CONDITION_OPTIONS } from '../constants/propertyScan';

const fieldSx = {
  '& .MuiInputLabel-root': {
    position: 'static', transform: 'none', color: '#8A8F98', fontSize: 11,
    textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, marginBottom: '6px',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#8A8F98' },
  '& .MuiOutlinedInput-root': {
    color: '#F5F7FA', bgcolor: '#1A1D21', borderRadius: '10px',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: '1px solid #00AAFF' },
  },
};

export default function PropertyScanFormPage() {
  const navigate = useNavigate();
  const [scanId, setScanId] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [propertyName, setPropertyName] = useState('');
  const [surfaceType, setSurfaceType] = useState('');
  const [existingCoating, setExistingCoating] = useState('');
  const [condition, setCondition] = useState('');
  const [colorSheen, setColorSheen] = useState('');

  const analyseFile = useCallback(async (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);

    setAnalysing(true);
    try {
      let id = scanId;
      if (!id) {
        const scan = await api.createPropertyScan({});
        id = scan.id;
        setScanId(id);
      }
      const result = await api.analyzePropertyScan(id, file);
      setSurfaceType(result.surface_type ?? '');
      setExistingCoating(result.existing_coating ?? '');
      setCondition(result.current_condition ?? '');
      toast.success('Surface analyzed');
    } catch {
      toast.error('Analysis failed');
    } finally {
      setAnalysing(false);
    }
  }, [scanId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
    disabled: analysing,
    onDrop: (files) => { const file = files[0]; if (file) analyseFile(file); },
  });

  const removeImage = () => {
    setImagePreview(null);
    setScanId(null);
    setSurfaceType(''); setExistingCoating(''); setCondition('');
  };

  const canSubmit = Boolean(imagePreview) && !analysing && propertyName.trim().length > 0;

  const submit = async () => {
    if (!scanId || !canSubmit) return;
    setSubmitting(true);
    try {
      await api.updatePropertyScan(scanId, {
        property_name: propertyName.trim(),
        surface_type: surfaceType || undefined,
        existing_coating: existingCoating || undefined,
        current_condition: (condition || undefined) as any,
        color_sheen: colorSheen.trim() || undefined,
      });
      navigate(`/property-scans/${scanId}`);
    } catch {
      toast.error('Could not save scan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h5" sx={{ color: '#F5F7FA', fontWeight: 'bold', mb: 3 }}>New Scan</Typography>

      <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {imagePreview ? (
            <Box sx={{ position: 'relative' }}>
              <Box component="img" src={imagePreview} alt="Surface preview"
                sx={{ width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 1.5, display: 'block' }} />
              <IconButton
                aria-label="Remove image"
                onClick={removeImage}
                disabled={analysing}
                sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(10,10,12,0.75)', color: '#F5F7FA', '&:hover': { bgcolor: '#0A0A0C' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
              {analysing && (
                <Box sx={{
                  position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1.5,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CircularProgress sx={{ color: '#00AAFF', mb: 1 }} />
                  <Typography sx={{ color: '#F5F7FA', fontSize: 13 }}>Analyzing surface…</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              {...getRootProps()}
              data-testid="property-scan-dropzone"
              sx={{
                textAlign: 'center', py: 6, cursor: 'pointer', borderRadius: 1.5,
                border: `2px dashed ${isDragActive ? '#00AAFF' : 'rgba(255,255,255,0.15)'}`,
                bgcolor: isDragActive ? 'rgba(0,170,255,0.06)' : 'transparent',
              }}>
              <input {...getInputProps()} data-testid="property-scan-dropzone-input" />
              <Box sx={{
                width: 56, height: 56, borderRadius: 1.5, bgcolor: 'rgba(0,170,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
              }}>
                <CloudUploadIcon sx={{ color: '#00AAFF', fontSize: 28 }} />
              </Box>
              <Typography sx={{ color: '#F5F7FA', fontWeight: 600, mb: 0.5 }}>Drop your surface image here</Typography>
              <Typography sx={{ color: '#6E7681', fontSize: 13 }}>or click to browse · PNG, JPG up to 10MB</Typography>
            </Box>
          )}

          <TextField
            label="Property Name" fullWidth required
            placeholder="e.g. Tacoma Medical Center"
            value={propertyName} onChange={(e) => setPropertyName(e.target.value)}
            sx={fieldSx} InputLabelProps={{ shrink: true }} />
          <TextField
            select label="Surface Type" fullWidth
            value={surfaceType} onChange={(e) => setSurfaceType(e.target.value)}
            sx={fieldSx} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">Select…</MenuItem>
            {SURFACE_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField
            select label="Existing Coating" fullWidth
            value={existingCoating} onChange={(e) => setExistingCoating(e.target.value)}
            sx={fieldSx} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">Select…</MenuItem>
            {COATING_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField
            select label="Condition" fullWidth
            value={condition} onChange={(e) => setCondition(e.target.value)}
            sx={fieldSx} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">Select…</MenuItem>
            {CONDITION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Color / Sheen" fullWidth
            placeholder="e.g. SW 7004 / Satin"
            value={colorSheen} onChange={(e) => setColorSheen(e.target.value)}
            sx={fieldSx} InputLabelProps={{ shrink: true }} />

          <Button
            variant="contained" onClick={submit} disabled={!canSubmit || submitting}
            sx={{ bgcolor: '#00AAFF', borderRadius: 999, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0090DD' }, alignSelf: 'flex-start', px: 4 }}>
            {submitting ? <CircularProgress size={20} /> : 'Create Scan'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
